<script lang="ts">
// this must be exported here for the keep alive on reroute
export default {
  name: "DealerDetail",
};
</script>
<script setup lang="ts">
import ChildService from "@/api/child";
import DealerService, { DealerContactType, DefaultDealer, type TDealer, type TDealerContact } from "@/api/dealer";
import DeviceService, { type TDevice } from "@/api/device";
import FamilyService from "@/api/family";
import RoleService from "@/api/role";
import ServiceService, { type TService } from "@/api/service";
import UserService, { type TUser } from "@/api/user";
import AddEditDealerForm from "@/components/models/dealer/AddEditDealerForm.vue";
import DealerContactsTable from "@/components/models/dealer/DealerContactsTable.vue";
import DevicesTable from "@/components/models/device/DeviceTable.vue";
import ServicesTable from "@/components/models/service/ServicesTable.vue";
import UsersTable from "@/components/models/user/UsersTable.vue";
import { i18n } from "@/locales";
import { ActionsEnum, SubjectsEnum, type AppAbility } from "@/services/ability";
import { useSnackStore } from "@/stores/snack";
import { generateFormOptions, type PaginatedType, type VFormFieldChoice, type VFormOptions } from "@/utils/DjangoTypes";
import { DefaultServerTableParams, type ServerTableParamsType } from "@/utils/TableUtils";
import { useAbility } from "@casl/vue";
import { useQuery } from "@tanstack/vue-query";
import { definePage } from "unplugin-vue-router/runtime";
import { onMounted, provide, ref } from "vue";
import { useRoute } from "vue-router";

const route = useRoute();

const currentDealerId = Number(route.params.id);

definePage({
  meta: {
    title: "Detail of Dealer",
    checkPrivileges: () => {
      const { can } = useAbility<AppAbility>();
      return can(ActionsEnum.USE, SubjectsEnum.DEALER);
    },
  },
});

const { t } = i18n.global;

const snackStore = useSnackStore();
const fetching = ref(true);

const entity = ref<TDealer>(DefaultDealer);

const dealerChoices = ref<VFormFieldChoice<any>[]>([]);
const dealerOptions = ref<VFormOptions>({});
const dealerServiceChoices = ref<VFormFieldChoice<any>[]>([]);
const dealerContactOptions = ref<VFormOptions>({});

const deviceChoices = ref<VFormFieldChoice<any>[]>([]);

const serviceTypeChoices = ref<VFormFieldChoice<any>[]>([]);

const userOptions = ref<VFormOptions>({});
const childChoices = ref<VFormFieldChoice<any>[]>([]);
const familiesChoices = ref<VFormFieldChoice<any>[]>([]);

const rolesChoices = ref<VFormFieldChoice<any>[]>([]);

const queriesTableParams: Record<string, ServerTableParamsType> = {
  devices: {
    ...DefaultServerTableParams,
    filter: {
      dealer: currentDealerId,
    },
  },
  services: {
    ...DefaultServerTableParams,
    filter: {
      dealers: currentDealerId,
    },
  },
  users: {
    ...DefaultServerTableParams,
    filter: {
      dealer: currentDealerId,
    },
  },
  dealerContacts: {
    ...DefaultServerTableParams,
    filter: {
      dealer: currentDealerId,
      type: DealerContactType.CONTACT,
    },
  },
};
provide("queriesTableParams", queriesTableParams);

const {
  data: devices,
  isPending: isDevicesPending,
  isFetching: isDevicesFetching,
} = useQuery({
  queryKey: ["devices"],
  queryFn: async (): Promise<PaginatedType<TDevice[]> | undefined> => {
    try {
      const [result, childSelectable] = await Promise.all([
        DeviceService.device.getAll(queriesTableParams.devices),
        ChildService.child.getAllSelectable(),
      ]);

      childChoices.value = childSelectable;

      return result.data;
    } catch (error) {
      snackStore.showError(error);
    }
  },
});

const {
  data: services,
  isPending: isServicesPending,
  isFetching: isServicesFetching,
} = useQuery({
  queryKey: ["services"],
  queryFn: async (): Promise<PaginatedType<TService[]> | undefined> => {
    try {
      const [result, devices] = await Promise.all([
        ServiceService.service.getAll(queriesTableParams.services),
        DeviceService.device.getAllSelectable(),
      ]);

      deviceChoices.value = devices;
      dealerServiceChoices.value = ServiceService.service.makeSelectable(result.data.items);

      return result.data;
    } catch (error) {
      snackStore.showError(error);
    }
  },
});

const {
  data: users,
  isPending: isUsersPending,
  isFetching: isUsersFetching,
} = useQuery({
  queryKey: ["users"],
  queryFn: async (): Promise<PaginatedType<TUser[]> | undefined> => {
    try {
      const [result, options, rolesSelectable, familiesSelectable, dealersSelectable] = await Promise.all([
        UserService.user.getAll(queriesTableParams.users),
        UserService.user.getOptions(),
        RoleService.role.getAllSelectable(),
        FamilyService.family.getAllSelectable(),
        DealerService.dealer.getAllSelectable(),
      ]);

      userOptions.value = generateFormOptions(options.data.actions.POST);
      rolesChoices.value = rolesSelectable;
      familiesChoices.value = familiesSelectable;
      dealerChoices.value = dealersSelectable;

      return result.data;
    } catch (error) {
      snackStore.showError(error);
    }
  },
});

const {
  data: dealerContacts,
  isPending: isDealerContactsPending,
  isFetching: isDealerContactsFetching,
} = useQuery({
  queryKey: ["dealerContacts"],
  queryFn: async (): Promise<PaginatedType<TDealerContact[]> | undefined> => {
    try {
      const [result, options] = await Promise.all([
        DealerService.contact.getAll(queriesTableParams.dealerContacts),
        DealerService.contact.getOptions(),
      ]);

      dealerContactOptions.value = generateFormOptions(options.data.actions.POST);

      return result.data;
    } catch (error) {
      snackStore.showError(error);
    }
  },
});

onMounted(async () => {
  const [dealer, options, dealersSelectable, serviceTypesSelectable] = await Promise.all([
    DealerService.dealer.get(currentDealerId),
    DealerService.dealer.getOptions(),
    DealerService.dealer.getAllSelectable(),
    ServiceService.serviceType.getAllSelectable(),
  ]);

  entity.value = dealer.data;
  dealerOptions.value = generateFormOptions(options.data.actions.POST);
  dealerChoices.value = dealersSelectable;
  serviceTypeChoices.value = serviceTypesSelectable;

  fetching.value = false;
});
</script>

<template>
  <v-container fluid>
    <div class="tw:!mb-10">
      <v-card>
        <v-card-title class="tw:!px-6 tw:!py-8">
          <div class="tw:text-3xl">{{ t("models.dealer.typeof") }}</div>
        </v-card-title>
        <v-card-text class="tw:!px-6 tw:!pb-8">
          <v-skeleton-loader
            :loading="fetching || isDealerContactsPending || isDealerContactsFetching"
            type="table-tbody"
          >
            <div class="w-100">
              <add-edit-dealer-form
                :item="entity"
                :loading="fetching"
                :options="dealerOptions"
              />
            </div>
          </v-skeleton-loader>
        </v-card-text>
      </v-card>

      <div class="tw:!mt-10">
        <dealer-contacts-table
          :query-key="'dealerContacts'"
          :data="dealerContacts"
          :default-dealer="currentDealerId"
          :options="dealerContactOptions"
          :loading="fetching || isDealerContactsPending || isDealerContactsFetching"
        />
      </div>

      <div class="tw:!mt-10">
        <devices-table
          :query-key="'devices'"
          :data="devices"
          :services="dealerServiceChoices"
          :dealers="dealerChoices"
          :children="childChoices"
          :default-dealer="currentDealerId"
          headline-text="models.dealer.deviceTable"
          headline-class="tw:text-1xl tw:-mb-6"
          :loading="fetching || isDevicesPending || isDevicesFetching"
        />
      </div>

      <div class="tw:!mt-10">
        <services-table
          :query-key="'services'"
          :data="services"
          :service-types="serviceTypeChoices"
          :dealers="dealerChoices"
          headline-text="models.dealer.serviceTable"
          headline-class="tw:text-1xl tw:-mb-6"
          :loading="fetching || isServicesPending || isServicesFetching"
        />
      </div>

      <div class="tw:!mt-10">
        <users-table
          :query-key="'users'"
          :data="users"
          :loading="fetching || isUsersPending || isUsersFetching"
          :families="familiesChoices"
          :dealers="dealerChoices"
          :roles="rolesChoices"
          headline-text="models.dealer.userTable"
          headline-class="tw:text-1xl tw:-mb-6"
          :options="userOptions"
        />
      </div>
    </div>
  </v-container>
</template>

<style scoped></style>
